const IDENTIFIER_KEYS = ['financingId', 'loanId', 'externalLoanId'] as const;

export type FinancingIdentifierKey = (typeof IDENTIFIER_KEYS)[number];

export type FinancingIdentifiers = Partial<Record<FinancingIdentifierKey, string>>;

function readNonEmptyString(obj: Record<string, unknown>, key: string): string | undefined {
    const value = obj[key];
    if (value == null) {
        return undefined;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? undefined : trimmed;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    return undefined;
}

function collectFromRecord(obj: Record<string, unknown>): FinancingIdentifiers {
    const result: FinancingIdentifiers = {};
    for (const key of IDENTIFIER_KEYS) {
        const v = readNonEmptyString(obj, key);
        if (v !== undefined) {
            result[key] = v;
        }
    }
    return result;
}

/**
 * Reads financingId, loanId, and externalLoanId from API JSON when present.
 * Checks the root object and a nested `data` object (common wrapped payloads).
 */
export function extractFinancingIdentifiersFromResponse(data: unknown): FinancingIdentifiers {
    if (data === null || typeof data !== 'object') {
        return {};
    }
    const root = data as Record<string, unknown>;
    const nested = root.data;
    const fromNested =
        nested !== null && typeof nested === 'object' && !Array.isArray(nested)
            ? collectFromRecord(nested as Record<string, unknown>)
            : {};
    const fromRoot = collectFromRecord(root);
    return { ...fromNested, ...fromRoot };
}
